import * as React from 'react';
import { useNavigate } from '@tanstack/react-router';
import { CheckCheck, Plus, Sparkles } from 'lucide-react';
import { ErrorMessage } from '@/components/ui/error-message';
import { Spinner } from '@/components/ui/spinner';
import { SubTabsNav } from '@/components/ui/sub-tabs-nav';
import {
  KeyHint,
  WorkspaceButton,
  WorkspaceDialog,
  WorkspaceHeader,
  WorkspaceLayout,
  useToast,
} from '@/components/ui/workspace';
import {
  SelectionTabs,
  type SelectionTab,
} from '@/components/engagement-requirements/selection-tabs';
import { ApiError } from '@/lib/api/client';
import { useEngagementQuery } from '@/lib/api/engagements';
import {
  useChangeRating,
  useConfirmRating,
  useConfirmRatings,
  useProposeRatings,
  useSetDispositions,
  useSetVendorProducts,
  useVendorIntelligenceQuery,
  type DispositionRow,
  type ProductChoice,
  type Rating,
  type VendorRating,
} from '@/lib/api/vendor-intelligence';
import { useVendorReportQuery } from '@/lib/api/vendor-intelligence-report';
import { AreaCellPanel } from './area-cell-panel';
import type { AreaCellRef } from './area-matrix';
import { NotMappedNote } from './not-assessed';
import { RatingPanel } from './rating-panel';
import { RatingsGrid, type GridCell } from './ratings-grid';
import { ReportStep, type ReportStepView } from './report/report-step';
import { ShortlistPart } from './shortlist-part';
import { VendorPicker } from './vendor-picker';
import { VendorsPart } from './vendors-part';
import {
  VI_INDEX_PATH,
  VI_STEP_PATH,
  VI_STEPS,
  allProposed,
  cellKey,
  defaultViStep,
  dispositionMixText,
  inBatches,
  pendingRequestOf,
  plural,
  ratingsByCell,
  judgeRunsByCell,
  ratingMixText,
  reportWaitsForRatings,
  reportWaitsForShortlist,
  viStatusLine,
  viStepStates,
  waitingCells,
  type PendingRequest,
  type ViStep,
} from './vi-logic';

export { VI_INDEX_PATH, VI_STEP_PATH };

/**
 * Engagement → Selection → Vendor intelligence (T-0267; E14-design §1,
 * §3; ADR-025 decisions 2, 3 and 4; four steps with a URL each in T-0272,
 * E24-design §2). One direction, one step per screen: who is in scope
 * (Vendors), how each fits each module (Ratings, the grid filling the
 * step), who comes out on top and whether that holds (Shortlist), and the
 * Report (T-0269, T-0271, T-0283: the published report, with the draft
 * editor as a second view), which waits for confirmed ratings and the
 * confirmed shortlist. The strip under the Selection tabs shows each step's state and a
 * status line of the server's counts; a locked step still opens and says
 * what it waits for.
 *
 * Propose ratings derives what the vendors' own answers can rate and sends
 * the rest to the AI; the tab reads the evaluation again every two seconds
 * until every requested cell has a newer rating or a finished run, on
 * whichever step the reader is. Every figure is the server's.
 */
export function VendorIntelligenceScreen({
  engagementId,
  step,
  openCell = null,
  reportView = 'published',
}: {
  engagementId: string;
  /** The step the URL names; null on the bare URL. */
  step: ViStep | null;
  /** `?product=&module=` on the Ratings step: the cell to open (a link from the area matrix). */
  openCell?: GridCell | null;
  /** `?view=draft` on the Report step opens the draft; the published report otherwise. */
  reportView?: ReportStepView;
}) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const engagementQuery = useEngagementQuery(engagementId);
  const [request, setRequest] = React.useState<PendingRequest | null>(null);
  const query = useVendorIntelligenceQuery(engagementId, { poll: request !== null });
  const evaluation = query.data;
  // The status line's "Draft v2"; the Report step reads the same cached query.
  const reportQuery = useVendorReportQuery(engagementId);
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const [selected, setSelected] = React.useState<GridCell | null>(null);
  const [areaCell, setAreaCell] = React.useState<AreaCellRef | null>(null);
  const [confirmAllOpen, setConfirmAllOpen] = React.useState(false);

  const setProducts = useSetVendorProducts(engagementId);
  const propose = useProposeRatings(engagementId);
  const confirm = useConfirmRating(engagementId);
  const confirmMany = useConfirmRatings(engagementId);
  const change = useChangeRating(engagementId);
  const setDispositions = useSetDispositions(engagementId);
  const busy =
    setProducts.isPending ||
    propose.isPending ||
    confirm.isPending ||
    confirmMany.isPending ||
    change.isPending ||
    setDispositions.isPending;

  const waiting = React.useMemo(
    () => (request && evaluation ? waitingCells(evaluation, request) : []),
    [request, evaluation],
  );
  const waitingKeys = React.useMemo(
    () => new Set(waiting.map((c) => cellKey(c.product_id, c.module_id))),
    [waiting],
  );
  // The request is followed until nothing it sent is still waiting.
  React.useEffect(() => {
    if (request && evaluation && waiting.length === 0) {
      setRequest(null);
      toast({
        message: 'The AI has finished proposing. Confirm the ratings to count them.',
      });
    }
  }, [request, evaluation, waiting.length, toast]);

  // The bare URL: once the evaluation is read, the first step not done is
  // the URL (replaced, so Back skips the bare one). Its body shows meanwhile.
  const landing = step === null && evaluation ? defaultViStep(evaluation, reportQuery.data) : null;
  React.useEffect(() => {
    if (landing) {
      void navigate({ to: VI_STEP_PATH, params: { engagementId, step: landing }, replace: true });
    }
  }, [landing, navigate, engagementId]);
  const shown: ViStep = step ?? landing ?? 'vendors';

  // The rating panel belongs to the Ratings step, the area panel to Shortlist.
  React.useEffect(() => {
    setSelected(null);
    setAreaCell(null);
  }, [shown]);

  // A link into the grid (`?product=&module=` from the area matrix's panel)
  // opens that cell once, when the evaluation knows it; closing the panel
  // then leaves it closed.
  const openKey = openCell ? cellKey(openCell.productId, openCell.moduleId) : null;
  const openedKey = React.useRef<string | null>(null);
  const loaded = evaluation !== undefined;
  React.useEffect(() => {
    if (shown !== 'ratings' || !openCell || !openKey || !loaded) return;
    if (openedKey.current === openKey) return;
    openedKey.current = openKey;
    setSelected(openCell);
  }, [shown, openCell, openKey, loaded]);

  // The Report step's two views: the published report, or the draft (`?view=draft`).
  const setReportView = React.useCallback(
    (view: ReportStepView) => {
      void navigate({
        to: VI_STEP_PATH,
        params: { engagementId, step: 'report' },
        search: view === 'draft' ? { view: 'draft' } : {},
      });
    },
    [navigate, engagementId],
  );

  const onSelectTab = React.useCallback(
    (tab: SelectionTab) => {
      if (tab === 'requirements') {
        void navigate({ to: '/engagements/$engagementId/selection', params: { engagementId } });
      }
    },
    [navigate, engagementId],
  );

  const engagementLabel = engagementQuery.data
    ? `${engagementQuery.data.client_name} · ${engagementQuery.data.name}`
    : null;
  const states = evaluation ? viStepStates(evaluation, reportQuery.data) : null;
  const header = (
    <>
      <WorkspaceHeader title="Vendor intelligence" meta={engagementLabel ?? undefined} />
      <SelectionTabs active="vendor-intelligence" showVendorIntelligence onSelect={onSelectTab} />
      <div className="border-t border-ink-100 px-6 pt-1.5">
        <SubTabsNav
          label="Vendor intelligence steps"
          active={shown}
          tabs={VI_STEPS.map((s) => ({
            key: s.slug,
            label: s.label,
            to: VI_STEP_PATH,
            params: { engagementId, step: s.slug },
            state: states?.[s.slug] ?? 'todo',
          }))}
        />
        <p className="m-0 py-1.5 text-meta text-ink-500" data-testid="vi-status-line">
          {evaluation ? viStatusLine(evaluation, reportQuery.data) : 'Loading…'}
        </p>
      </div>
    </>
  );

  if (query.error) {
    const off = query.error instanceof ApiError && query.error.code === 'not_found';
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
        {off ? (
          <>
            <h1 className="text-h2 font-semibold text-ink-900">Selection is not available here</h1>
            <p className="max-w-md text-body-sm text-ink-500">
              The Selection module is not enabled for this engagement, or you do not have access to
              it.
            </p>
          </>
        ) : (
          <>
            <ErrorMessage error={query.error} />
            <WorkspaceButton onClick={() => void query.refetch()}>Try again</WorkspaceButton>
          </>
        )}
      </div>
    );
  }
  if (!evaluation) {
    return (
      <WorkspaceLayout header={header}>
        <div className="flex flex-1 items-center justify-center p-8">
          <Spinner />
        </div>
      </WorkspaceLayout>
    );
  }

  const ratings = ratingsByCell(evaluation);
  const runs = judgeRunsByCell(evaluation);
  const canEdit = evaluation.can_edit;
  const hasModules = evaluation.modules.length > 0;
  const hasProducts = evaluation.products.length > 0;

  const writeProducts = (choices: ProductChoice[]) =>
    setProducts.mutate(choices, {
      onSuccess: () => setPickerOpen(false),
    });
  const removeProduct = (productId: string) => {
    if (selected?.productId === productId) setSelected(null);
    writeProducts(
      evaluation.products
        .filter((p) => p.product_id !== productId)
        .map((p) => ({ product_id: p.product_id })),
    );
  };
  const addProducts = (choices: ProductChoice[]) =>
    writeProducts([...evaluation.products.map((p) => ({ product_id: p.product_id })), ...choices]);

  const runPropose = () =>
    propose.mutate(
      {},
      {
        onSuccess: (result) => {
          const derived = result.derived.length;
          setRequest(pendingRequestOf(result));
          toast({
            message:
              result.requested.length > 0
                ? `${derived > 0 ? `${plural(derived, 'rating')} rated from the vendors' answers. ` : ''}The AI is proposing ${plural(result.requested.length, 'more rating')}.`
                : `${plural(derived, 'rating')} rated from the vendors' answers.`,
          });
        },
        onError: (err) =>
          toast({ message: err instanceof Error ? err.message : 'Propose failed.' }),
      },
    );
  const confirmOne = (r: VendorRating) =>
    confirm.mutate(r.id, {
      onError: (err) => toast({ message: err instanceof Error ? err.message : 'Not confirmed.' }),
    });
  const confirmAll = (_productId: string, ids: string[]) =>
    confirmMany.mutate(ids, {
      onSuccess: (out) =>
        toast({ message: `${plural(out.confirmed.length, 'rating')} confirmed.` }),
      onError: (err) => toast({ message: err instanceof Error ? err.message : 'Not confirmed.' }),
    });
  // Every proposed rating in the grid, after a summary the consultant
  // confirms: a confirmed rating never goes back to proposed (it can still be
  // changed, with a reason). Sent in batches the endpoint takes.
  const proposedAll = allProposed(evaluation);
  const confirmEverything = async () => {
    let confirmed = 0;
    try {
      for (const batch of inBatches(proposedAll.map((r) => r.id))) {
        const out = await confirmMany.mutateAsync(batch);
        confirmed += out.confirmed.length;
      }
      setConfirmAllOpen(false);
      toast({ message: `${plural(confirmed, 'rating')} confirmed.` });
    } catch (err) {
      toast({ message: err instanceof Error ? err.message : 'Not confirmed.' });
    }
  };
  const changeOne = (r: VendorRating, next: Rating, reason: string) =>
    change.mutateAsync({ ratingId: r.id, rating: next, reason });

  const selProduct = selected
    ? evaluation.products.find((p) => p.product_id === selected.productId)
    : undefined;
  const selModule = selected
    ? evaluation.modules.find((m) => m.module_id === selected.moduleId)
    : undefined;
  const area = areaCell ? evaluation.areas.find((a) => a.key === areaCell.areaKey) : undefined;
  const areaRating =
    area && areaCell ? area.products.find((p) => p.product_id === areaCell.productId) : undefined;
  const areaProduct = areaCell
    ? evaluation.products.find((p) => p.product_id === areaCell.productId)
    : undefined;
  const panel =
    shown === 'ratings' && selected && selProduct && selModule ? (
      <RatingPanel
        product={selProduct}
        module={selModule}
        rating={ratings.get(cellKey(selected.productId, selected.moduleId)) ?? null}
        run={runs.get(cellKey(selected.productId, selected.moduleId)) ?? null}
        canEdit={canEdit}
        busy={busy}
        error={confirm.error ?? change.error}
        onClose={() => setSelected(null)}
        onConfirm={confirmOne}
        onChange={changeOne}
      />
    ) : shown === 'shortlist' && area && areaRating && areaProduct ? (
      <AreaCellPanel
        engagementId={engagementId}
        area={area}
        cell={areaRating}
        product={areaProduct}
        modules={evaluation.modules}
        onClose={() => setAreaCell(null)}
      />
    ) : undefined;

  // The shortlist (T-0274): Confirm shortlist confirms every proposed row
  // in one write; a changed row is saved alone with its reason.
  const confirmShortlist = (rows: DispositionRow[]) =>
    setDispositions.mutate(rows, {
      onSuccess: () => toast({ message: `Shortlist confirmed: ${dispositionMixText(rows)}.` }),
      onError: (err) =>
        toast({ message: err instanceof Error ? err.message : 'Shortlist not confirmed.' }),
    });
  const setDisposition = (row: DispositionRow) => setDispositions.mutateAsync([row]);

  const proposeBlocked = !hasProducts || !hasModules || request !== null;
  const waitingBox = (text: string) => (
    <p className="m-0 rounded-card border border-dashed border-ink-200 px-4 py-3 text-body-sm text-ink-500">
      {text}
    </p>
  );

  let toolbar: React.ReactNode;
  let footer: React.ReactNode;
  let body: React.ReactNode;
  switch (shown) {
    case 'vendors':
      toolbar = (
        <>
          <span className="text-body-sm text-ink-700">
            {plural(evaluation.products.length, 'vendor')} in scope
          </span>
          {canEdit ? (
            <WorkspaceButton
              className="ml-auto"
              onClick={() => setPickerOpen(true)}
              disabled={busy}
            >
              <Plus className="h-3.5 w-3.5" aria-hidden="true" />
              Add vendors
            </WorkspaceButton>
          ) : null}
        </>
      );
      body = (
        <section aria-label="Vendors" className="flex flex-col gap-3 px-6 py-5">
          <VendorsPart
            products={evaluation.products}
            canEdit={canEdit}
            busy={busy}
            onRemove={removeProduct}
          />
          {setProducts.error ? <ErrorMessage error={setProducts.error} /> : null}
        </section>
      );
      break;
    case 'ratings':
      toolbar = (
        <>
          {hasProducts && hasModules ? (
            <span className="text-body-sm text-ink-700">
              {evaluation.counts.confirmed.toLocaleString('en-US')} confirmed ·{' '}
              {evaluation.counts.proposed.toLocaleString('en-US')} AI-proposed, waiting for you ·{' '}
              {evaluation.counts.unrated.toLocaleString('en-US')} not rated
            </span>
          ) : null}
          {request ? (
            <span
              role="status"
              className="inline-flex items-center gap-1.5 rounded-chip border border-ai-surface-border bg-ai-surface px-2 py-0.5 text-meta text-ai-ink"
            >
              <Spinner className="h-3.5 w-3.5" />
              Proposing ratings: {request.requested.length - waiting.length} of{' '}
              {request.requested.length} done
            </span>
          ) : null}
          {canEdit ? (
            <div className="ml-auto flex flex-wrap items-center gap-2">
              {proposedAll.length > 0 ? (
                <WorkspaceButton disabled={busy} onClick={() => setConfirmAllOpen(true)}>
                  <CheckCheck className="h-3.5 w-3.5" aria-hidden="true" />
                  Confirm all proposed ({proposedAll.length.toLocaleString('en-US')})
                </WorkspaceButton>
              ) : null}
              <WorkspaceButton
                variant="primary"
                disabled={proposeBlocked || busy}
                onClick={runPropose}
              >
                <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
                Propose ratings
              </WorkspaceButton>
            </div>
          ) : null}
        </>
      );
      footer =
        hasProducts && hasModules ? (
          <>
            <KeyHint keys={['←', '→', '↑', '↓']}>move</KeyHint>
            <KeyHint keys={['Enter']}>open</KeyHint>
            <KeyHint keys={['c']}>confirm</KeyHint>
            <KeyHint keys={['Esc']}>close panel</KeyHint>
          </>
        ) : undefined;
      body = (
        <section aria-label="Ratings" className="flex min-h-0 flex-1 flex-col gap-3 px-6 py-4">
          {!hasModules ? (
            <>
              {waitingBox(
                'Add requirements first: ratings are per library module, and no module holds a requirement that carries weight yet.',
              )}
              <NotMappedNote data={evaluation.not_assessed} />
            </>
          ) : !hasProducts ? (
            waitingBox('Choose at least two vendors to rate them module by module.')
          ) : (
            <>
              <RatingsGrid
                className="flex-1"
                evaluation={evaluation}
                waiting={waitingKeys}
                selected={selected}
                busy={busy}
                onOpen={setSelected}
                onConfirm={confirmOne}
                onConfirmAll={confirmAll}
              />
              <NotMappedNote data={evaluation.not_assessed} />
            </>
          )}
        </section>
      );
      break;
    case 'shortlist':
      body = (
        <section aria-label="Shortlist" className="px-6 py-5">
          <ShortlistPart
            evaluation={evaluation}
            canEdit={canEdit}
            busy={busy}
            error={setDispositions.error}
            selectedCell={areaCell}
            onOpenCell={setAreaCell}
            onConfirmShortlist={confirmShortlist}
            onSetDisposition={setDisposition}
          />
        </section>
      );
      break;
    case 'report':
      body = (
        <ReportStep
          engagementId={engagementId}
          ratingsReady={!reportWaitsForRatings(evaluation)}
          engagement={
            engagementQuery.data
              ? { client_name: engagementQuery.data.client_name, name: engagementQuery.data.name }
              : null
          }
          shortlistReady={!reportWaitsForShortlist(evaluation)}
          view={reportView}
          onView={setReportView}
        />
      );
      break;
  }

  return (
    <>
      <WorkspaceLayout header={header} toolbar={toolbar} panel={panel} footer={footer}>
        {body}
      </WorkspaceLayout>
      <WorkspaceDialog
        open={confirmAllOpen}
        onOpenChange={(open) => (busy ? undefined : setConfirmAllOpen(open))}
        title={`Confirm ${plural(proposedAll.length, 'proposed rating')}?`}
        description="Each one then counts in the fit. A confirmed rating can still be changed, with a reason, but it does not go back to proposed."
        actions={
          <>
            <WorkspaceButton
              variant="tertiary"
              size="md"
              disabled={busy}
              onClick={() => setConfirmAllOpen(false)}
            >
              Cancel
            </WorkspaceButton>
            <WorkspaceButton
              variant="primary"
              size="md"
              disabled={busy || proposedAll.length === 0}
              onClick={() => void confirmEverything()}
            >
              {confirmMany.isPending
                ? 'Confirming…'
                : `Confirm ${plural(proposedAll.length, 'rating')}`}
            </WorkspaceButton>
          </>
        }
      >
        <p className="m-0 text-body-sm text-ink-700">
          {ratingMixText(proposedAll)}, across{' '}
          {plural(new Set(proposedAll.map((r) => r.product_id)).size, 'vendor')}.
        </p>
      </WorkspaceDialog>
      {pickerOpen ? (
        <VendorPicker
          engagementId={engagementId}
          onClose={() => setPickerOpen(false)}
          onAdd={addProducts}
          busy={setProducts.isPending}
          error={setProducts.error}
        />
      ) : null}
    </>
  );
}
